import React, { useState, useEffect } from 'react';
import { PackagePlus, Sparkles, Loader2, Search, ShoppingCart, Plus, Trash2, Check, Truck, Boxes } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const CATEGORIEEN = [
  'Filters', 'Remmen', 'Accu & elektrisch', 'Distributie', 'Ophanging & besturing',
  'Verlichting', 'Olie & vloeistoffen', 'Bougies & ontsteking', 'Uitlaat', 'Koeling',
  'Aandrijving', 'Ruitenwissers & ruiten',
];

const inp = { padding: '9px 11px', border: '1px solid var(--cs-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default function OnderdelenCatalogus() {
  const { t } = useT();
  const { leveranciers, createInkoopOrder } = useData();
  const [v, setV] = useState({ kenteken: '', merk: '', model: '', bouwjaar: '', motor: '' });
  const [rdwBusy, setRdwBusy] = useState(false);
  const [cat, setCat] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [cart, setCart] = useState([]);
  const [levId, setLevId] = useState('');
  const [creating, setCreating] = useState(false);
  const [voorraadBusy, setVoorraadBusy] = useState(false);
  const [voorraad, setVoorraad] = useState(null); // gematchte voorraad-onderdelen
  const upd = (k, val) => setV(p => ({ ...p, [k]: val }));

  const norm = (s) => (s == null ? '' : String(s)).toLowerCase().trim();
  const matchVoertuig = (p) => {
    const merk = norm(v.merk), model = norm(v.model);
    if (!merk) return null;
    const afk = norm(p.afkomstigVoertuig);
    const tekst = [p.naam, p.omschrijving, p.merk, p.model].map(norm).join(' ');
    const pastList = Array.isArray(p.pastOp)
      ? p.pastOp.map(x => norm(typeof x === 'string' ? x : `${x.merk || ''} ${x.model || ''} ${x.bouwjaar || ''}`))
      : [];
    if (pastList.some(x => x.includes(merk) && (!model || x.includes(model))))
      return { score: 3, reden: t('Compatibel ("past op")', 'Compatibel ("past op")') };
    if (afk.includes(merk) && (!model || afk.includes(model)))
      return { score: 3, reden: `${t('Afkomstig van', 'Afkomstig van')} ${p.afkomstigVoertuig}` };
    if (pastList.some(x => x.includes(merk)) || afk.includes(merk))
      return { score: 2, reden: t('Zelfde merk', 'Zelfde merk') };
    if (tekst.includes(merk) && (!model || tekst.includes(model)))
      return { score: 1, reden: t('Mogelijk (naam komt overeen)', 'Mogelijk (naam komt overeen)') };
    return null;
  };

  const checkVoorraad = async () => {
    if (!v.merk.trim()) { toast.error(t('Vul eerst een voertuig in (kenteken of merk)', 'Vul eerst een voertuig in (kenteken of merk)')); return; }
    setVoorraadBusy(true); setVoorraad(null);
    try {
      const parts = await carsysApi.getParts();
      const matches = (parts || [])
        .filter(p => !p.verkochtOp && !p.verkocht)
        .map(p => ({ p, m: matchVoertuig(p) }))
        .filter(x => x.m)
        .sort((a, b) => b.m.score - a.m.score);
      setVoorraad(matches);
      if (!matches.length) toast.info(t('Geen passende onderdelen in voorraad gevonden', 'Geen passende onderdelen in voorraad gevonden'));
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setVoorraadBusy(false); }
  };

  const haalRdw = async () => {
    if (!v.kenteken.trim()) { toast.error(t('Vul een kenteken in', 'Vul een kenteken in')); return; }
    setRdwBusy(true);
    try {
      const info = await carsysApi.rdwLookup(v.kenteken.trim());
      if (!info?.merk) { toast.info(t('Geen RDW-gegevens gevonden', 'Geen RDW-gegevens gevonden')); return; }
      setV(p => ({
        ...p, merk: info.merk || p.merk,
        model: info.handelsbenaming || p.model,
        bouwjaar: info.datumEersteToelating ? String(info.datumEersteToelating).slice(0, 4) : p.bouwjaar,
        motor: info.cilinderinhoud ? `${info.cilinderinhoud} cc` : p.motor,
      }));
      toast.success(`${info.merk} ${info.handelsbenaming}`);
    } catch (e) { toast.error(t('RDW mislukt', 'RDW mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setRdwBusy(false); }
  };

  const zoek = async (categorie) => {
    if (!v.merk.trim()) { toast.error(t('Vul eerst een voertuig in (kenteken of merk)', 'Vul eerst een voertuig in (kenteken of merk)')); return; }
    setCat(categorie); setBusy(true); setResult(null);
    try { setResult(await carsysApi.partsCatalogus({ ...v, categorie })); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const inCart = (naam) => cart.some(c => c.naam === naam);
  const toggleCart = (o) => setCart(c => inCart(o.naam)
    ? c.filter(x => x.naam !== o.naam)
    : [...c, { naam: o.naam, code: o.oemIndicatie || '', omschrijving: o.omschrijving || '', aantal: 1, inkoopPrijs: Number(o.richtprijs) || 0 }]);

  const maakInkooporder = async () => {
    if (!levId) { toast.error(t('Kies een leverancier', 'Kies een leverancier')); return; }
    if (!cart.length) return;
    setCreating(true);
    try {
      const lev = leveranciers.find(l => l.id === levId);
      await createInkoopOrder({
        leverancierId: levId, leverancierNaam: lev?.naam || '', status: 'concept',
        notitie: `Catalogus: ${[v.merk, v.model, v.kenteken].filter(Boolean).join(' ')}`,
        regels: cart.map(c => ({ ...c })),
      });
      toast.success(t('Inkooporder (concept) aangemaakt', 'Inkooporder (concept) aangemaakt'), { description: `${cart.length} ${t('regels', 'regels')}` });
      setCart([]);
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setCreating(false); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PackagePlus size={20} /></div>
          <div><h2>{t('Onderdelen-catalogus', 'Onderdelen-catalogus')}</h2><p>{t('Bladeren per categorie en voertuig, met koppeling naar je leveranciers', 'Bladeren per categorie en voertuig, met koppeling naar je leveranciers')}</p></div>
        </div>
      </div>

      {/* Voertuig */}
      <div className="cs-card padded" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label className="cs-section-title">{t('Kenteken', 'Kenteken')}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={{ ...inp, width: '100%', textTransform: 'uppercase' }} value={v.kenteken} onChange={e => upd('kenteken', e.target.value.toUpperCase())} placeholder="12-AB-34" data-testid="cat-kenteken" />
              <button className="cs-btn" onClick={haalRdw} disabled={rdwBusy} data-testid="cat-rdw">{rdwBusy ? <Loader2 size={14} className="spin" /> : 'RDW'}</button>
            </div>
          </div>
          <div style={{ flex: '1 1 120px' }}><label className="cs-section-title">{t('Merk', 'Merk')}</label><input style={{ ...inp, width: '100%' }} value={v.merk} onChange={e => upd('merk', e.target.value)} data-testid="cat-merk" /></div>
          <div style={{ flex: '1 1 120px' }}><label className="cs-section-title">{t('Model', 'Model')}</label><input style={{ ...inp, width: '100%' }} value={v.model} onChange={e => upd('model', e.target.value)} /></div>
          <div style={{ flex: '0 1 90px' }}><label className="cs-section-title">{t('Bouwjaar', 'Bouwjaar')}</label><input style={{ ...inp, width: '100%' }} value={v.bouwjaar} onChange={e => upd('bouwjaar', e.target.value)} /></div>
          <div style={{ flex: '1 1 120px' }}><label className="cs-section-title">{t('Motor', 'Motor')}</label><input style={{ ...inp, width: '100%' }} value={v.motor} onChange={e => upd('motor', e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 12, borderTop: '1px solid var(--cs-border)', paddingTop: 12 }}>
          <button className="cs-btn" onClick={checkVoorraad} disabled={voorraadBusy} data-testid="cat-check-voorraad"
            style={{ background: '#0ea5e9', color: '#fff', borderColor: '#0ea5e9' }}>
            {voorraadBusy ? <Loader2 size={14} className="spin" /> : <Boxes size={14} />} {t('Check mijn voorraad voor dit voertuig', 'Check mijn voorraad voor dit voertuig')}
          </button>
        </div>
      </div>

      {voorraad && (
        <div className="cs-card" style={{ marginBottom: 16 }} data-testid="cat-voorraad-result">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--cs-border)' }}>
            <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#0ea5e9' }}>
              <Boxes size={16} /> {t('In voorraad — past op', 'In voorraad — past op')} {[v.merk, v.model].filter(Boolean).join(' ')} ({voorraad.length})
            </div>
            <button className="cs-btn ghost" onClick={() => setVoorraad(null)} data-testid="cat-voorraad-close"><Trash2 size={13} /> {t('Sluiten', 'Sluiten')}</button>
          </div>
          {voorraad.length === 0 ? (
            <div className="cs-empty" style={{ padding: 24 }}>{t('Geen passende onderdelen in voorraad.', 'Geen passende onderdelen in voorraad.')}</div>
          ) : (
            <table className="cs-table">
              <thead><tr><th>{t('Onderdeel', 'Onderdeel')}</th><th>{t('Match', 'Match')}</th><th>{t('Locatie', 'Locatie')}</th><th style={{ textAlign: 'right' }}>{t('Prijs', 'Prijs')}</th></tr></thead>
              <tbody>
                {voorraad.map(({ p, m }, i) => (
                  <tr key={p.id || i} data-testid={`cat-voorraad-item-${i}`}>
                    <td data-label={t('Onderdeel', 'Onderdeel')}><div style={{ fontWeight: 600 }}>{p.naam}</div><div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{p.oemNummer || p.onderdeelNummer || ''}</div></td>
                    <td data-label={t('Match', 'Match')}><span className={`cs-badge ${m.score >= 3 ? 'green' : m.score === 2 ? 'blue' : 'amber'}`}>{m.reden}</span></td>
                    <td data-label={t('Locatie', 'Locatie')} style={{ fontSize: 12 }}>{p.locatie || '—'}</td>
                    <td data-label={t('Prijs', 'Prijs')} style={{ textAlign: 'right', fontWeight: 600 }}>{eur(p.vraagPrijs || p.verkoopPrijs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 18, alignItems: 'start' }}>
        <div>
          {/* Categorieën */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {CATEGORIEEN.map(c => (
              <button key={c} className="cs-btn" style={{ background: cat === c ? '#7c3aed' : '#fff', color: cat === c ? '#fff' : 'inherit', borderColor: cat === c ? '#7c3aed' : 'var(--cs-border)' }} onClick={() => zoek(c)} data-testid={`cat-chip-${c}`}>{c}</button>
            ))}
          </div>

          {busy && <div className="cs-card padded" style={{ textAlign: 'center', padding: 40 }}><Loader2 size={28} className="spin" style={{ color: '#7c3aed' }} /><p style={{ color: 'var(--cs-text-muted)', marginTop: 8 }}>{t('AI zoekt onderdelen…', 'AI zoekt onderdelen…')}</p></div>}
          {!busy && !result && <div className="cs-card padded" style={{ textAlign: 'center', color: 'var(--cs-text-muted)', padding: 40 }}><Search size={32} style={{ opacity: 0.3 }} /><p style={{ marginTop: 10 }}>{t('Vul een voertuig in en kies een categorie.', 'Vul een voertuig in en kies een categorie.')}</p></div>}

          {result && (
            <div className="cs-card" data-testid="cat-result">
              <table className="cs-table">
                <thead><tr><th>{t('Onderdeel', 'Onderdeel')}</th><th>OEM</th><th>{t('Interval', 'Interval')}</th><th style={{ textAlign: 'right' }}>{t('Richtprijs', 'Richtprijs')}</th><th></th></tr></thead>
                <tbody>
                  {(result.onderdelen || []).map((o, i) => (
                    <tr key={i} data-testid={`cat-item-${i}`}>
                      <td><div style={{ fontWeight: 600 }}>{o.naam}</div><div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{o.omschrijving}</div></td>
                      <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{o.oemIndicatie || '—'}</td>
                      <td style={{ fontSize: 12 }}>{o.interval || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(o.richtprijs)}</td>
                      <td><button className="cs-btn ghost" onClick={() => toggleCart(o)} data-testid={`cat-add-${i}`} style={{ color: inCart(o.naam) ? 'var(--cs-green, #16a34a)' : undefined }}>{inCart(o.naam) ? <Check size={14} /> : <Plus size={14} />}</button></td>
                    </tr>
                  ))}
                  {(result.onderdelen || []).length === 0 && <tr><td colSpan={5} className="cs-empty">{t('Geen onderdelen gevonden.', 'Geen onderdelen gevonden.')}</td></tr>}
                </tbody>
              </table>
              {result.let_op && <div style={{ padding: '8px 12px', fontSize: 11.5, color: '#b45309' }}>{result.let_op}</div>}
            </div>
          )}
        </div>

        {/* Bestellijst */}
        <div className="cs-card padded" style={{ position: 'sticky', top: 16 }} data-testid="cat-cart">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 10 }}><ShoppingCart size={16} /> {t('Bestellijst', 'Bestellijst')} ({cart.length})</div>
          {cart.length === 0 ? <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)' }}>{t('Voeg onderdelen toe met de + knop.', 'Voeg onderdelen toe met de + knop.')}</p> : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, maxHeight: 240, overflowY: 'auto' }}>
                {cart.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid var(--cs-border)', paddingBottom: 5 }}>
                    <span style={{ flex: 1 }}>{c.naam}</span>
                    <span style={{ color: 'var(--cs-text-muted)' }}>{eur(c.inkoopPrijs)}</span>
                    <button onClick={() => setCart(cc => cc.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
              <label className="cs-section-title"><Truck size={12} /> {t('Leverancier', 'Leverancier')}</label>
              <select style={{ ...inp, width: '100%', marginBottom: 10 }} value={levId} onChange={e => setLevId(e.target.value)} data-testid="cat-leverancier">
                <option value="">-- {t('Kies leverancier', 'Kies leverancier')} --</option>
                {(leveranciers || []).map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
              </select>
              <button className="cs-btn primary" style={{ width: '100%' }} onClick={maakInkooporder} disabled={creating} data-testid="cat-maak-order">
                {creating ? <Loader2 size={14} className="spin" /> : <ShoppingCart size={14} />} {t('Maak inkooporder', 'Maak inkooporder')}
              </button>
              {(leveranciers || []).length === 0 && <p style={{ fontSize: 11, color: '#b45309', marginTop: 8 }}>{t('Voeg eerst een leverancier toe onder Inkoop.', 'Voeg eerst een leverancier toe onder Inkoop.')}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
